import { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, RotateCcw, X, Printer } from '../../icons';
import {
  approveDocument,
  extractErrorMessage,
  getDocument,
  recallDocument,
  submitDraft as submitDraftApi,
  deleteDraft as deleteDraftApi,
  rejectDocument,
} from '../../api/approval';
import type { ApprovalDocumentDetail, ApprovalLineDetail } from '../../types/approval';
import { Button, Stepper, Textarea, type StepperItem, type StepStatus } from '../../components/shared';
// 결재 A2(2026-08-24) — 첨부 목록·내려받기는 공용 컴포넌트가 한다(채팅·메일과 같은 것).
import { AttachmentList } from '../../components/Attachment';
// Cycle 43 — lang-aware date locale
import { subscribeSharedLang, getSharedLang, formatDateTime } from '../../lib/base-i18n';
import { tApproval, type ApprovalI18nKey } from '../../lib/approval-i18n';
import { sanitizeMailHtml } from '../../lib/sanitize';
import { approvalLines, lineSlotLabel, referrerLines } from '../../lib/approval-line-slot';
// 상신 본문은 한 곳에서 만든다 — 결재자 목록을 «결재 줄만»으로 거르는 안전장치가 거기 있다.
import { buildDraftSubmitBody } from '../../lib/approval-submit-body';
// 결재란 — 도장 줄 (2026-08-28 · 판 8). 종이 결재란처럼 「누가 승인했나」를 한눈에 보인다.
import { ApprovalStampRow } from './ApprovalStampRow';
import { useAuthStore } from '../../store/authStore';
// 서식 · 이 문서가 쓴 기준 (2026-08-28 · 판 9). 옛 문서는 둘 다 없어 아무것도 안 그린다.
import { ApprovalFormView, ApprovalBasisPanel } from './ApprovalFormView';
import { ApprovalHistory } from './ApprovalHistory';
import { Tabs } from '../../components/shared';

/**
 * Approval 결재선 status → Stepper status 매핑.
 * WAITING → 진행 중인 단계가 본 step 인지에 따라 active 또는 pending.
 */
function lineToStepperStatus(
  lineStatus: string,
  isCurrentStep: boolean,
): StepStatus {
  if (lineStatus === 'APPROVED') return 'done';
  if (lineStatus === 'REJECTED') return 'rejected';
  if (lineStatus === 'SKIPPED') return 'skipped';
  if (lineStatus === 'WAITING') return isCurrentStep ? 'active' : 'pending';
  return 'pending';
}

// Cycle 50 — line status enum 만 SSOT, label 은 tApproval 로 lang-aware (render 시점 결정).
const LINE_STATUS_LABEL_KEY: Record<string, ApprovalI18nKey> = {
  WAITING: 'approval.line_status.WAITING',
  APPROVED: 'approval.line_status.APPROVED',
  REJECTED: 'approval.line_status.REJECTED',
  SKIPPED: 'approval.line_status.SKIPPED',
};

interface Props {
  documentId: string;
  currentUserId: string;
  onChanged: () => void;
}

export function ApprovalDetail({ documentId, currentUserId, onChanged }: Props) {
  // 내 이름 — 이름 도장 미리보기에 쓴다(내 칸에서 서명을 즉석으로 만든다).
  const myName = useAuthStore((s) => s.user?.name ?? '');
  const [doc, setDoc] = useState<ApprovalDocumentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  /**
   * **잘못은 아닌데 알려야 하는 것** (2026-09-04 · 예산 판 B3).
   *
   * ★★ `error` 와 갈라 둔다 — `error` 는 **화면을 통째로 대체**한다(위 `if (error) return`).
   *    「예산에 못 붙었다」는 상신이 **끝난 뒤의 알림**이라 그렇게 하면 문서가 사라진다.
   */
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * **단추를 눌러 실패한 것** (2026-09-13 · ⑦) — 상신·결재·회수가 4xx 로 되돌아온 경우.
   *
   * ★★ 이것도 `error` 와 갈라 둔다. 종전엔 `error` 에 넣어 **문서가 통째로 사라지고 오류 한 줄만** 남았다 —
   *    상신이 실패하면 문서는 그대로 DRAFT 인데 화면에서는 없어진 것처럼 보여 다시 올릴 길도 안 보였다.
   *    `error` 는 이제 «문서를 못 읽었다» 에만 쓴다. 이것은 단추 곁에 한 줄로 서고 문서는 남는다.
   */
  const [actionError, setActionError] = useState<string | null>(null);
  /** 「문서」와 「이력」 — 이력은 **누를 때** 읽는다(2026-09-04 · 판 P6). */
  const [tab, setTab] = useState('doc');
  const [comment, setComment] = useState('');
  // Cycle 43 — lang 변경 시 re-render
  const [, setLangBump] = useState(getSharedLang());
  useEffect(() => subscribeSharedLang(() => setLangBump(getSharedLang())), []);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getDocument(documentId);
      setDoc(d);
    } catch (err) {
      setError(extractErrorMessage(err));
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const handleAction = useCallback(
    async (action: 'approve' | 'reject' | 'recall' | 'submit' | 'delete') => {
      if (!doc) return;
      setActing(true);
      setError(null);
      setActionError(null);
      try {
        if (action === 'approve') await approveDocument(doc.id, comment || undefined);
        else if (action === 'reject') await rejectDocument(doc.id, comment || undefined);
        else if (action === 'submit') {
          // ★ 결재선은 **여기서** 확정된다 — 임시보관은 빈 채로도 저장되지만 상신은 결재 1명이 있어야 한다.
          /*
           * ★★★ **결재자로는 `APPROVE` 줄만 싣는다** (2026-09-13 · 백엔드 #1189 §13 실측 결함).
           *
           *    종전 필터 `!== 'REFER'` 는 다른 종류의 줄까지 `approverIds` 로 보냈다. 줄은 step 순이라
           *    그 사람이 결재선 **맨 뒤 = 최종결재자**가 됐다(실측 문서 84: `{"approverIds":["5","1"]}` →
           *    totalSteps 1→2). `?? 'APPROVE'` 는 lineType 을 안 주는 옛 서버를 위한 것이다.
           * ⛔ **지금도 같은 위험이 있다 — 참고자(REFER)다.** 참고자는 «살아 있고»(운영 13줄)
           *   섞이면 «보기만 하던 사람»이 문서를 멈추는 사람이 된다. 그래서 그 거르개는
           *   `lib/approval-submit-body.ts` 에 «안전장치»로 남아 있고 시험이 지킨다.
           * ⚠ 2026-09-27 — 「`agreerIds` 는 바뀐 게 없으면 안 싣는다」는 종전 규칙은 **없어졌다**.
           *   합의 기능을 다 지워 **그 칸 자체가 없다**(#1774 · #1779).
           */
          const body = buildDraftSubmitBody(doc.lines);
          if (body.approverIds.length === 0) {
            throw new Error('결재자가 없습니다 — 기안 화면에서 결재선을 골라 다시 만들어 주세요.');
          }
          const res = await submitDraftApi(doc.id, body);
          /**
           * ★★ **상신에서도 예산 이야기를 한다** (2026-09-04 · 예산 판 B3).
           *    지출 행은 «상신할 때» 만들어지므로(임시보관에는 없다) 예산에 못 붙는 것도 이때
           *    처음 드러난다. 여기서 말하지 않으면 **기안 화면으로 낸 것과 다른 안내**가 된다.
           * ★ 「못 붙었다」는 잘못이 아니라 사실이라 **상신 자체는 그대로 끝난다.**
           */
          const budgetNotes = res.expenseRequest?.notes ?? [];
          if (budgetNotes.length > 0) setNotice(budgetNotes.join('\n'));
        } else if (action === 'delete') {
          if (!window.confirm('이 임시보관을 지웁니다. 되돌릴 수 없습니다. 계속할까요?')) {
            setActing(false); return;
          }
          await deleteDraftApi(doc.id);
          onChanged();
          return;
        }
        else await recallDocument(doc.id);
        setComment('');
        await refetch();
        onChanged();
      } catch (err) {
        // ★ 문서는 남긴다 — 실패는 단추 곁에만(위 actionError 주석). DRAFT 는 서버에서도 그대로다.
        setActionError(extractErrorMessage(err));
      } finally {
        setActing(false);
      }
    },
    [doc, comment, refetch, onChanged],
  );

  if (loading) return <p className="approval-detail__hint">불러오는 중…</p>;
  if (error) return <p className="approval-detail__error">{error}</p>;
  if (!doc) return null;

  /** 상신은 끝났고 예산만 안 붙은 경우 — 문서를 가리지 않고 위에 한 줄 얹는다. */
  const budgetNotice = notice ? (
    <div className="approval-detail__notice" role="status">
      <b>예산에는 붙지 않았습니다.</b>
      {notice.split('\n').map((n) => <span key={n}>{n}</span>)}
      <small>지출 기록은 남습니다 — 나중에 예산이 생기면 담당자가 이어 붙일 수 있습니다.</small>
    </div>
  ) : null;

  const isDrafter = doc.drafterId === currentUserId;
  // F-3 (review): myActionable 은 backend 가 effective approver (직접 + 위임 chain) 평가 후
  // 결정 — frontend 는 그대로 신뢰. 위임 사용자도 line.approverId !== me 이지만 actionable=true.
  const canApprove = doc.myActionable;
  const canReject = doc.myActionable;
  const canRecall =
    isDrafter && (doc.status === 'DRAFT' || doc.status === 'IN_PROGRESS');
  /**
   * **임시보관** — 아직 아무에게도 안 갔다 (2026-09-04 · 판 P2).
   *
   * ★★ 여기서만 「올리기」와 「지우기」가 뜬다. 상신된 문서는 지워지지 않는다(서버가 막는다) —
   *    결재자가 본 문서가 목록에서 사라지면 「내가 본 그 문서는 뭐였나」에 아무도 못 답한다.
   */
  const isDraft = isDrafter && doc.status === 'DRAFT';

  /**
   * ★★★ **결재자가 예산을 함께 본다** (2026-09-04 · 예산 판 B4).
   *
   *    기안 화면이 「올릴 수는 있습니다 — 결재자가 이 사실을 함께 봅니다」라고 말한다.
   *    여기 안 그리면 그 문장이 거짓이 된다 — **아무도 안 보는 표시는 표시가 아니다.**
   * ★★ **막지 않는다**(결정 B5-a) — 결재 버튼은 그대로다. 판단 재료를 얹을 뿐이다.
   * ★ 지출결의서가 아니거나 옛 서버면 아무것도 안 그린다.
   */
  const budget = doc.budget;
  const budgetBlock = budget && (budget.over.length > 0 || budget.unlinked > 0) ? (
    <div className="approval-detail__budget" role="status">
      {budget.over.map((o) => (
        <span key={o.budgetName} className="approval-detail__budget-over">
          «{o.budgetName}» 예산을 <b>{o.amount.toLocaleString()}원</b> 넘습니다.
        </span>
      ))}
      {budget.unlinked > 0 && (
        <span>내역 {budget.unlinked}줄이 <b>예산에 붙지 않았습니다.</b></span>
      )}
      <small>예산은 결재를 막지 않습니다 — 판단에 참고하시라고 적습니다.</small>
    </div>
  ) : null;

  return (
    <div className="approval-detail">
      {budgetNotice}
      {budgetBlock}
      <header className="approval-detail__header">
        <div className="approval-detail__title-row">
          {doc.isUrgent && <span className="approval-detail__urgent">{tApproval('approval.urgent_badge')}</span>}
          <h2>{doc.title}</h2>
          {/*
            ★★ **인쇄** (2026-08-29 · 판 8 잔여) — 결재 문서를 종이로 남기는 자리.
               판 8 이 도장을 화면에만 그려 두어, 출력하면 결재란이 통째로 빠졌다.
            ★ PDF 라이브러리를 넣지 않는다 — 증명서(`CertificateDoc`)와 같은 방식으로
              `@media print` 가 이 조각만 남긴다. 도장 그림은 `print-color-adjust: exact` 로 지킨다.
          */}
          <Button
            variant="secondary"
            size="compact"
            className="approval-detail__print"
            onClick={() => window.print()}
            title="이 결재 문서를 인쇄합니다"
            iconLeft={<Printer size={15} aria-hidden />}
          >
            인쇄
          </Button>
        </div>
        <div className="approval-detail__meta">
          {/*
            ★★ **문서번호** (2026-09-04 · 판 P4). 종이로 나갔을 때 「그 번호 문서」로 부를 수
               있어야 한다 — 그래서 제목 바로 밑, 인쇄에도 실리는 자리에 둔다.
            ★ **없는 것이 정상인 경우가 둘**이다: 임시보관(아직 안 갔다) · 이 판 이전 문서
               (백필하지 않았다 — 그때 그 번호로 결재된 적이 없는데 있는 것처럼 되면 안 된다).
               그때는 **번호 자리를 아예 안 그린다**(「없음」이라고 적으면 문제처럼 읽힌다).
          */}
          {doc.docNo && (
            <>
              <span className="approval-detail__docno">{doc.docNo}</span>
              <span>·</span>
            </>
          )}
          <span>{doc.type}</span>
          <span>·</span>
          <span>
            {tApproval('approval.detail.progress_prefix')} {doc.currentStep}/{doc.totalSteps}
          </span>
          <span>·</span>
          <span className={`approval-detail__status approval-detail__status--${doc.status}`}>
            {doc.status}
          </span>
        </div>
      </header>

      {/*
        ★ 결재란이 **먼저**다 — 문서를 열었을 때 제일 먼저 보는 것이 「결재를 받았나」다.
          단계 목록(아래)은 그 다음이다.
      */}
      <ApprovalStampRow
        doc={doc}
        myUserId={currentUserId}
        myName={myName}
        onSignatureChanged={() => { void refetch(); }}
      />

      <ApprovalLineStepper doc={doc} />

      {/* 서식에 채운 값 — 본문 **위**다. 종류마다 다른 칸이 그 문서의 알맹이다(판 9). */}
      <ApprovalFormView fields={doc.form?.fields ?? []} data={doc.formData} />

      <section className="approval-detail__body" aria-label={tApproval('approval.detail.body_aria')}>
        <h3>{tApproval('approval.detail.body_heading')}</h3>
        {doc.contentType === 'HTML' ? (
          /*
           * Cycle 62 — HTML 본문은 렌더 직전 DOMPurify 로 sanitize (XSS 방어, defense in depth).
           * MailDetail 과 동일 정책(backend sanitize + frontend sanitize 양층, 한쪽 우회 시에도
           * 차단). 기존엔 doc.content 를 raw 주입하며 backend SSOT 만 가정했으나, approval
           * 본문은 backend 에서도 sanitize 안 됐던 stored XSS 표면이었음 (mail 과 비대칭).
           */
          <div
            className="approval-detail__content"
            // eslint-disable-next-line react/no-danger -- sanitizeMailHtml 통과 후 안전.
            dangerouslySetInnerHTML={{ __html: sanitizeMailHtml(doc.content) }}
          />
        ) : (
          <pre className="approval-detail__content approval-detail__content--text">
            {doc.content}
          </pre>
        )}

        {/* 첨부 (결재 A2, 2026-08-24) — 목록·내려받기는 공용 컴포넌트가 한다(채팅·메일과 같은 것).
            열 수 있는 사람은 파일 권한 사다리의 8번 칸이 가른다: 기안자 · 결재선(참고자 포함) · 공개. */}
        {(doc.attachments?.length ?? 0) > 0 && (
          <div className="approval-detail__attachments">
            <span className="approval-detail__label">첨부 {doc.attachments!.length}</span>
            <AttachmentList attachments={doc.attachments} shortType />
          </div>
        )}
      </section>

      {/* 이 문서가 쓴 기준 — **본문 아래, 접어서**(판 9). 평소엔 볼 일이 없고
          「왜 이렇게 됐나」를 물을 때만 펼친다. 옛 문서에는 없어 아무것도 안 그린다. */}
      <ApprovalBasisPanel basis={doc.basis} />

      {/*
        ★★★ **이력** (2026-09-04 · 판 P6 · 결정 ⑧) — 「누가 언제 무엇을 했나」.
           지금까지 화면은 **「지금 어디까지 갔나」** 만 말했다(결재란·단계). 「이 문서 왜
           반려됐지」를 물으면 답할 자리가 없었다 — 그 사실은 감사 로그에만 있었다.
        ★★ **표를 새로 만들지 않았다**(결정 ⑧-a) — 감사 로그가 정본이고 해시 사슬이라 못 고친다.
        ★ **탭을 눌렀을 때만** 읽는다 — 문서를 열 때마다 감사 표를 훑을 이유가 없다.
      */}
      <section className="approval-detail__tabs" aria-label="문서 · 이력">
        <Tabs
          items={[{ value: 'doc', label: '문서' }, { value: 'history', label: '이력' }]}
          value={tab}
          onChange={setTab}
          ariaLabel="문서 · 이력"
        />
        {tab === 'history' && <ApprovalHistory documentId={doc.id} />}
      </section>

      {(canApprove || canReject || canRecall || isDraft) && (
        <section className="approval-detail__actions" aria-label={tApproval('approval.detail.actions_aria')}>
          {(canApprove || canReject) && (
            <Textarea
              fullWidth
              wrapperClassName="approval-detail__comment"
              placeholder={tApproval('approval.detail.comment_placeholder')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              maxLength={2000}
            />
          )}
          {actionError && <p className="approval-detail__error" role="alert">{actionError}</p>}
          <div className="approval-detail__buttons">
            {canApprove && (
              <Button
                disabled={acting}
                onClick={() => void handleAction('approve')}
                iconLeft={<Check size={14} aria-hidden />}
              >
                {tApproval('approval.detail.btn_approve')}
              </Button>
            )}
            {canReject && (
              <Button
                variant="danger"
                disabled={acting}
                onClick={() => void handleAction('reject')}
                iconLeft={<X size={14} aria-hidden />}
              >
                {tApproval('approval.detail.btn_reject')}
              </Button>
            )}
          {/*
            ⛔ 합의 전용 단추 쌍(「동의」·「반대」)과 그 안내문을 지웠다 (2026-09-27 · 합의 기능 삭제).
              서버가 그 길을 닫았다(#1774) — 남겨 두면 404 를 부르는 단추가 된다.
          */}
            {/*
              ★★ **임시보관에만** 뜬다 (판 P2) — 올리면 결재선이 확정되고 근태 행·알림이 그때
                 생긴다. 지우는 것은 아무에게도 안 갔을 때만이다.
            */}
            {isDraft && (
              <>
                <Button
                  disabled={acting}
                  onClick={() => void handleAction('submit')}
                  iconLeft={<Check size={14} aria-hidden />}
                >
                  올리기
                </Button>
                {/* ★ 지우기는 «아무에게도 안 갔을 때만» 뜬다 — 종전대로 담담하게 둔다. */}
                <Button
                  variant="secondary"
                  disabled={acting}
                  onClick={() => void handleAction('delete')}
                  iconLeft={<X size={14} aria-hidden />}
                >
                  지우기
                </Button>
              </>
            )}
            {canRecall && !isDraft && (
              <Button
                variant="secondary"
                disabled={acting}
                onClick={() => void handleAction('recall')}
                iconLeft={<RotateCcw size={14} aria-hidden />}
              >
                {tApproval('approval.detail.btn_recall')}
              </Button>
            )}
          </div>
          {isDraft && (
            <p className="approval-detail__hint">
              <b>아직 아무에게도 가지 않았습니다.</b> 올리면 그때 결재자에게 알림이 가고,
              근태·보고 신청도 그때 만들어집니다.
            </p>
          )}
        </section>
      )}
    </div>
  );
}

/*
 * ⚠ **2026-09-04(판 P1) — 「결재」 한 낱말로는 자리를 못 말한다.**
 *   결재 줄이 여럿이면 **마지막 하나만 「최종결재자」**이고 그 앞은 「결재자」다. 셈은
 *   `lib/approval-line-slot.ts` 한 곳에 있다 — 화면마다 세면 같은 줄이 다른 이름으로 뜬다.
 */

/**
 * 결재선에 그릴 이름 (2026-08-28 · 판 6).
 *
 * ★ 옛 서버는 `approver` 를 안 준다 → 종전대로 번호(화면이 멎지 않는다).
 * ★ 지운 계정이면 `null` — 줄은 남는다(지우면 결재 이력이 끊긴다).
 * ★ 직위는 있으면 붙인다 — 결재선에서 「누가 위인가」가 곧 뜻이다.
 */
function approverLabel(l: ApprovalLineDetail): string {
  if (l.approver === undefined) return `결재자 ${l.approverId}`;
  if (l.approver === null) return '(지워진 계정)';
  return l.approver.position ? `${l.approver.name} · ${l.approver.position}` : l.approver.name;
}

interface ApprovalLineStepperProps {
  doc: ApprovalDocumentDetail;
}

/**
 * 결재선 → 공통 Stepper 컴포넌트로 표시 (slfg motion 사이클 옵션 B).
 * 본 cycle 의 1차 채택. 다른 페이지 (Task project 단계 등) 도 같은 컴포넌트 사용 가능.
 */
function ApprovalLineStepper({ doc }: ApprovalLineStepperProps) {
  const steps = useMemo<StepperItem[]>(
    () =>
      // ★ 참고자는 단계가 아니다 — 아래 명단으로 뺀다(2026-09-04 · 판 P1).
      approvalLines(doc.lines).map((l, i, arr) => ({
        id: String(l.id),
        /**
         * ★★ **번호가 아니라 사람** (2026-08-28 · 판 6).
         *    「결재자 12」로는 누가 보고 있는지 아무도 못 읽는다.
         *
         *  · 옛 서버(`approver` 없음) → 종전대로 번호. 화면이 멎지 않는다
         *  · 지운 계정(`null`) → 「(지워진 계정)」. 줄을 지우면 결재 이력이 끊긴다
         *  · 직위는 있으면 붙인다 — 결재선에서 「누가 위인가」가 곧 뜻이다
         */
        label: approverLabel(l),
        sub: (
          <>
            {/* 결재자 · 최종결재자 · 합의 — 자리는 순서가 정한다(마지막 결재 줄 하나만 「최종결재자」). */}
            {lineSlotLabel(arr, i)} ·{' '}
            {LINE_STATUS_LABEL_KEY[l.status] ? tApproval(LINE_STATUS_LABEL_KEY[l.status]!) : l.status}
            {l.comment && <> · {l.comment}</>}
            {l.processedAt && <> · {formatDateTime(l.processedAt)}</>}
          </>
        ),
        status: lineToStepperStatus(l.status, l.step === doc.currentStep),
      })),
    [doc.lines, doc.currentStep],
  );

  /**
   * ★★★ **참고자는 결재선이 아니다 — 하위에 명단으로만** (2026-09-04 · 사용자 결정).
   *
   *    *「참조는 결재를 하지 않고 하위에 보는 것이다」* · *「하위에 참조자 명단만 보이면 된다」*
   *    Stepper 에 섞어 그리면 상태 없는 칸이 줄에 껴서 **「저 사람이 안 눌러서 멈춰 있다」**로
   *    읽힌다. 그래서 단계에서 빼고 아래에 이름만 늘어놓는다.
   */
  const referrers = referrerLines(doc.lines);

  return (
    <section className="approval-detail__lines" aria-label="결재선">
      <h3>결재선</h3>
      <Stepper steps={steps} aria-label="결재선 단계" />

      {referrers.length > 0 && (
        <p className="approval-detail__referrers">
          <span className="approval-detail__referrers-label">참고자</span>
          {referrers.map((l) => approverLabel(l)).join(' · ')}
          <small> · 결재하지 않고 문서를 볼 수만 있습니다</small>
        </p>
      )}
    </section>
  );
}
